/**
 * Shared geometry-document mutation helpers.
 *
 * Extracted from `geometryReducer.ts` so the sub-reducers
 * (`arrayReducers`, `geometryReducerEdit`, `geometryReducerEditTrim`,
 * `geometryReducerLayout`) can pull these primitives in without
 * re-importing the parent reducer module — breaks the depcruise cycles
 *
 *   geometryReducer.ts ↔ arrayReducers.ts
 *   geometryReducer.ts ↔ geometryReducerEdit.ts
 *   geometryReducer.ts ↔ geometryReducerEditTrim.ts
 *   geometryReducer.ts ↔ geometryReducerLayout.ts
 *
 * The parent `geometryReducer.ts` re-exports every symbol so existing
 * import paths keep working.
 */

import {
  assembleDrawingDocument,
  BLOCK_EDIT_SPACE_ID,
  drawingEntitySchema,
  recomputeNativeClipPath,
  REFEDIT_SPACE_ID,
  resetNestedRecordIdentity,
  type DrawingDocument,
  type DrawingEntity,
  type DrawingEntityInput,
  type EntityColor,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  initCustomPropertiesFromMetadata,
  invalidateViewportClip,
  resolveVpclipBoundaryPolygon,
} from "@dxf-ai-editor/command-engine";
import { detachInvalidatedViewportClipObjects } from "@dxf-ai-editor/drawing-model";

/**
 * Whether `spaceId` names a real PAPER-SPACE LAYOUT (DrawingSpace
 * `kind === "layout"`) in `document`. The single fail-closed source of truth for
 * the LAYOUT-ONLY MVIEW rule: a Viewport entity may only be created in a paper
 * layout — never in model space, a block-edit / refedit session, or an orphan
 * id. Shared by the geometry reducer (`geometryReducerLayout`) AND the shell
 * reducer (`geometryShellReducer`) so an invalid `geometry/create-viewport`
 * (spaceId = "model" / orphan) is rejected on BOTH paths — without the shell
 * mirror the geometry reducer's NO_MUTATIONS no-op fast path would preserve a
 * shell-only phantom selected viewport (#287-11).
 */
export function isPaperSpaceLayoutTarget(
  document: DrawingDocument,
  spaceId: string,
): boolean {
  const targetSpace = document.spaces.find((space) => space.id === spaceId);
  return targetSpace?.kind === "layout";
}

/**
 * One-truth dense-id allocator over a FLAT entity list. Computes the next
 * `${prefix}-${n}` id the same way AutoCAD-style dense numbering does: start at
 * `count(entities whose type.toLowerCase() === prefix) + 1`, then linear-probe
 * `n++` while the candidate id is already taken.
 *
 * Non-block-aware primitive kept for any pure "flat list, no document"
 * caller. {@link nextGeometryEntityId} and the shell's `nextEntityId`
 * (`reducerHelpers.ts`) do NOT call this directly — they call
 * {@link nextDenseEntityIdWithBlockSeed}, the block-namespace-aware sibling
 * that stays algorithmically identical but avoids re-flattening/re-scanning
 * `document.blocks` on every mint (N5, local Codex round-4 P1).
 */
export function nextDenseEntityId(
  entities: ReadonlyArray<{ id: string; type: string }>,
  prefix: string,
): string {
  const existingIds = new Set(entities.map((entity) => entity.id));
  let counter =
    entities.filter((entity) => entity.type.toLowerCase() === prefix).length + 1;
  let candidate = `${prefix}-${counter}`;

  while (existingIds.has(candidate)) {
    counter += 1;
    candidate = `${prefix}-${counter}`;
  }

  return candidate;
}

/**
 * Precomputed id-namespace contribution of every BlockDefinition's child
 * entities in `document.blocks` — the `ids` every candidate mint must avoid,
 * and the per-lowercased-type population `nextDenseEntityIdWithBlockSeed`
 * adds to its local-scope count. Immutable — callers must never mutate the
 * returned Set/Map (they may be a live cache entry; see
 * {@link getBlockNamespaceSeed}).
 */
export interface BlockNamespaceSeed {
  readonly ids: ReadonlySet<string>;
  readonly typeCounts: ReadonlyMap<string, number>;
}

/**
 * {@link BlockNamespaceSeed} for `document`, REFERENCE-IDENTITY CACHED on
 * `document.blocks` (N5, local Codex round-3+round-4). AutoCAD assigns one
 * handle per entity for the WHOLE drawing, including entities that live only
 * inside a block table record and are never instantiated into a space
 * directly — a block-definition child's id can therefore never be safely
 * re-minted for a new space entity. After BSAVE/BCLOSE "parks"
 * BEDIT-authored geometry into `blocks[name].entities` (the transient
 * block-edit space is dropped entirely — see `blockEditReducer.ts`), that
 * content exists ONLY here, so a `document.spaces`-only scan could re-mint
 * an id already live inside a block definition — a real id collision (the
 * exact scenario `editorStore.beditActiveSpace.test.ts`'s BEDIT/BSAVE/BCLOSE
 * regression exercises).
 *
 * A SINGLE dispatch calls this twice — once from the shell reducer's
 * `nextEntityId`, once from the geometry reducer's `nextGeometryEntityId` —
 * both against the SAME pre-mutation `geometryDocument.blocks`. Round-3
 * cached only the flattened entity LIST, but `nextDenseEntityId` still
 * re-mapped/re-filtered/re-built a `Set` over it on every call — on a
 * block-heavy document (a large imported block definition, e.g. a title
 * block, or a streamed-import drawing whose block defs live outside the
 * compact space cache) that left an O(total block-child entities) rescan on
 * every ordinary single-entity create (Codex round-4 P1). This caches the
 * FULLY-PROCESSED `{ids, typeCounts}` instead, so a mint against an
 * unchanged `document.blocks` does zero block-side work beyond the `WeakMap`
 * lookup + a couple of O(1) Set/Map reads.
 *
 * `document.blocks` is a NEW object reference only when a mutation actually
 * touches it (the reducers' immutable-update convention — `{...document,
 * spaces: ...}` without touching `blocks` leaves `nextDoc.blocks ===
 * prevDoc.blocks`), so this turns "recompute on every create" into
 * "recompute only when blocks actually change" — O(1) amortized for the
 * common repeated-create-with-unchanged-blocks workflow (e.g. drawing
 * several lines in a row), and needs no manual eviction (`WeakMap` entries
 * are GC'd once a `blocks` object is no longer referenced by any live
 * document / undo-history entry).
 *
 * DELIBERATELY NOT DEDUPED BY ID (Codex round-1 P2). `dependencyGraph.ts`
 * documents that "an entity id can legally repeat across containers" — a
 * block-definition child and a model-space entity, or two DIFFERENT block
 * definitions' children, may share a bare id while being genuinely distinct
 * entities of possibly DIFFERENT types (that is why `DependencyIndex` keys on
 * the composite `(kind, container, id)`, never a bare id). Every block-child
 * entity therefore contributes exactly one count toward its OWN type in
 * `typeCounts`, regardless of any other entity (in a space or a different
 * block) sharing its bare id — order-independent, since Map accumulation via
 * `+1` per element is commutative. The single known side-effect is a
 * relocated LAYOUT block's documented `*Model_Space` / paired
 * `*Paper_Space<N>` pairing (`dxfBridge.annotativeExport.ts`'s
 * `buildAnnotativeObjectsGraph` doc comment), where the importer keeps a
 * verbatim copy of the SAME entity on both a paired space AND its block
 * definition — that duplicate is counted once here AND once by the
 * caller's own space-scoped scan, seeding the counter one higher than
 * strictly minimal. That is cosmetic only (the probe loop still skips every
 * truly-live id regardless of the seed), never a collision.
 */
const blockNamespaceSeedCache = new WeakMap<
  DrawingDocument["blocks"],
  BlockNamespaceSeed
>();

export function getBlockNamespaceSeed(document: DrawingDocument): BlockNamespaceSeed {
  const cached = blockNamespaceSeedCache.get(document.blocks);
  if (cached !== undefined) {
    return cached;
  }
  const ids = new Set<string>();
  const typeCounts = new Map<string, number>();
  for (const blockDef of Object.values(document.blocks)) {
    for (const entity of blockDef.entities) {
      ids.add(entity.id);
      const typeKey = entity.type.toLowerCase();
      typeCounts.set(typeKey, (typeCounts.get(typeKey) ?? 0) + 1);
    }
  }
  const seed: BlockNamespaceSeed = { ids, typeCounts };
  blockNamespaceSeedCache.set(document.blocks, seed);
  return seed;
}

/**
 * Whether a SPACE-scoped `entity` is a LIVE MIRROR of content
 * {@link getBlockNamespaceSeed} already counts, and must therefore be
 * excluded from the space-scoped scan — or every mirrored entity would be
 * counted TWICE (N5, local Codex round-5 P2).
 *
 * `BLOCK_EDIT_SPACE_ID` (BEDIT) and `REFEDIT_SPACE_ID` (REFEDIT) are BOTH
 * excluded PER-ENTITY, ONLY when the entity's id is ALSO already in
 * `blockSeed.ids` — never by space membership alone. Two DIFFERENT reasons
 * an entity currently sitting in one of these synthetic spaces may NOT yet
 * be mirrored into its block definition, and must therefore stay counted:
 *
 *   1. TIMING (REFEDIT). `enterRefeditSpace` (`helpers.ts`) does NOT sync
 *      new authoring back into `blocks[refeditBlockName].entities` until
 *      `exitRefeditSpace`'s save path — a `geometry/create-*` issued
 *      mid-session exists ONLY in the refedit space for the REST of that
 *      session (Codex round-6 P1).
 *
 *   2. UNSUPPORTED BLOCK-CHILD TYPE (BEDIT). `syncBlockEditSpaceToDefinition`
 *      (`blockEditReducer.ts`) parses each block-edit-space entity through
 *      `blockChildEntitySchema` and SILENTLY DROPS anything that doesn't fit
 *      the block-child union (e.g. Wipeout / Viewport — types AutoCAD does
 *      not allow inside a block definition) — that entity stays in the
 *      block-edit space FOREVER (every subsequent sync drops it again the
 *      same way) but NEVER reaches `blocks[blockEditName].entities`. An
 *      earlier revision excluded `BLOCK_EDIT_SPACE_ID` UNCONDITIONALLY on
 *      the (incomplete) assumption that BEDIT's after-every-command sync
 *      always mirrors everything; a second Wipeout created in the same
 *      session then silently re-minted the first one's id and threw a real
 *      `ZodError: Duplicate entity id` out of `assembleDrawingDocument`
 *      (Codex round-7 P1).
 *
 * The SAME per-entity id-membership check handles both: an id genuinely
 * mirrored (BEDIT's normal case, or REFEDIT's still-synced pre-existing
 * children) is in `blockSeed.ids` and gets excluded; anything NOT yet (or
 * never going to be) mirrored is absent from `blockSeed.ids` and stays
 * counted — no space-kind branching needed.
 *
 * `BLOCK_TEST_SPACE_ID` (TESTBLOCK) is DELIBERATELY NEVER excluded (falls
 * through to `false` below without an id-membership check): its synthetic
 * space holds exactly one preview INSERT with a fixed id
 * (`BLOCK_TEST_INSERT_ID`), never a copy of `blockDef.entities` — nothing to
 * double-count, and its id still needs to be reserved like any other live
 * entity.
 */
function isBlockNamespaceMirroredEntity(
  entity: { id: string; spaceId: string },
  blockSeed: BlockNamespaceSeed,
): boolean {
  if (entity.spaceId === BLOCK_EDIT_SPACE_ID || entity.spaceId === REFEDIT_SPACE_ID) {
    return blockSeed.ids.has(entity.id);
  }
  return false;
}

/**
 * {@link nextDenseEntityId}'s algorithm, EXCEPT the "already taken" /
 * "already counted" universe is `scopedEntities` (a caller-local-scope flat
 * list — `document.spaces` flattened for {@link nextGeometryEntityId}, or
 * `state.drawing.entities` for the shell's `nextEntityId`), FIRST filtered
 * through {@link isBlockNamespaceMirroredEntity} (N5, local Codex
 * round-5/round-6), then UNIONED with a precomputed {@link BlockNamespaceSeed}
 * instead of a single concatenated array. Byte-identical output to the old
 * `nextDenseEntityId([...scopedEntities, ...blockChildEntities], prefix)`
 * MINUS live-mirrored entries (both the membership set and the type-count
 * seed are the same union / sum, just computed without re-flattening or
 * re-scanning the (potentially cached, potentially large) block portion on
 * every call — N5, local Codex round-4 P1).
 */
export function nextDenseEntityIdWithBlockSeed(
  scopedEntities: ReadonlyArray<{ id: string; type: string; spaceId: string }>,
  prefix: string,
  blockSeed: BlockNamespaceSeed,
): string {
  const countedEntities = scopedEntities.filter(
    (entity) => !isBlockNamespaceMirroredEntity(entity, blockSeed),
  );
  const scopedIds = new Set(countedEntities.map((entity) => entity.id));
  let counter =
    countedEntities.filter((entity) => entity.type.toLowerCase() === prefix).length +
    (blockSeed.typeCounts.get(prefix) ?? 0) +
    1;
  let candidate = `${prefix}-${counter}`;

  while (scopedIds.has(candidate) || blockSeed.ids.has(candidate)) {
    counter += 1;
    candidate = `${prefix}-${counter}`;
  }

  return candidate;
}

export function nextGeometryEntityId(
  document: DrawingDocument,
  prefix: string,
): string {
  return nextDenseEntityIdWithBlockSeed(
    document.spaces.flatMap((space) => space.entities),
    prefix,
    getBlockNamespaceSeed(document),
  );
}

/**
 * Amortized-O(1) id minter that reproduces {@link nextGeometryEntityId}'s
 * EXACT id sequence over a batch of appends — the byte-identity gate for the
 * T6a perf refactor (the in-reducer multi-append loops: COPY / MIRROR / OFFSET
 * / ARRAY{RECT,POLAR,PATH} / EXPLODE).
 *
 * ## Why this exists
 *
 * The per-entity loops minted each clone's id with `nextGeometryEntityId(doc,
 * prefix)`, which is O(N): it flattens `document.spaces`, builds a fresh
 * `Set<id>`, and re-counts the `type.toLowerCase() === prefix` population on
 * EVERY call (the `document.blocks` contribution is cached separately — see
 * {@link getBlockNamespaceSeed} — but the spaces portion, which mutates on
 * nearly every command, cannot be). Calling it K times inside a loop is
 * O(N·K) ≈ O(N²). The allocator pays that O(N) spaces scan ONCE at
 * construction and then mints in amortized O(1).
 *
 * ## Byte-identity contract (mandatory — entity.id IS the exported group-5
 * handle; many tests assert `line-1` / `circle-1` literals)
 *
 * `next(prefix)` replicates `nextGeometryEntityId` bit-for-bit:
 *   - The per-prefix counter SEEDS lazily on first use at
 *     `count(entities whose type.toLowerCase() === prefix) + 1` — the SAME
 *     base `nextGeometryEntityId` recomputes each call (keyed on the lowercased
 *     RAW type, NOT the caller-supplied prefix, so it matches even when a
 *     caller passes a `replace(/\s+/g,"-")`-normalized prefix).
 *   - It emits `${prefix}-${counter}`, then linear-probes `counter++` while the
 *     candidate is already live (same probe loop), then ADDS the minted id to
 *     the live set and STORES the advanced counter.
 *
 * Because each `next(prefix)` mints exactly one id of that prefix and the
 * caller appends exactly one entity of that type per mint, the stored
 * monotonic counter stays equal to `nextGeometryEntityId`'s recomputed
 * `typeCount + 1` base (both track skipped/colliding ids through the shared
 * live set). The dense case is therefore byte-identical, and the
 * sparse/collision case lands on the identical probe result.
 *
 * The allocator must be seeded ONCE from the document the loop starts on and
 * then driven in the SAME iteration order the per-entity loop minted ids, so
 * the id sequence is preserved exactly.
 */
export interface EntityIdAllocator {
  next(prefix: string): string;
}

/**
 * T9b review round 3 (P1) — clone `entity` under a NEW id, resetting every
 * OWN-RECORD identity the clone must not share with its source. A DXF COMPLEX
 * ENTITY emits further records INSIDE itself, each with a group-5 of its own
 * that the parent carries as model data: an INSERT with attributes owns its
 * ATTRIB children (`attributes[].handle`) and its terminating SEQEND
 * (`seqendHandle`); a heavy POLYLINE owns every VERTEX record
 * (`vertices[].handle`) and its own terminating SEQEND. A plain
 * `{ ...entity, id }` spread (COPY, keep-source MIRROR, non-associative ARRAY)
 * duplicated those own handles onto every clone → two records with the same
 * group-5 on export, which is a corrupt file (strict readers reject it; ezdxf
 * silently re-mints, so it stays invisible until another application opens the
 * drawing). The clone's child handles are blanked / dropped; the export mints
 * fresh ones for it (`prepareImportedDocumentForExport` on the imported path,
 * the conformant skeleton on the from-scratch path).
 *
 * GitHub Codex review PR #785 round 3 (P1) — the reset is delegated to
 * `drawing-model`'s {@link resetNestedRecordIdentity}, which finds those slots
 * STRUCTURALLY (every nested own group-5 key, excluding the `{code, handle}`
 * reference carriers and raw group streams) instead of naming one entity type.
 * The previous revision handled only `"Block"`, so the heavy POLYLINE — added
 * later, with the same complex-entity shape — duplicated every VERTEX handle.
 * The export guard reads the SAME module (`emitsTerminatingSeqend`), so the
 * reducer and the bridge cannot disagree about what a clone owns.
 *
 * The single place the "fresh identity on clone" rule lives — every clone site
 * spreads through it. Pure: the source is untouched.
 */
export function cloneEntityWithFreshIdentity<T extends { id: string; type: string }>(
  entity: T,
  id: string,
): T {
  return { ...resetNestedRecordIdentity(entity), id };
}

export function createEntityIdAllocator(
  document: DrawingDocument,
): EntityIdAllocator {
  // The block-namespace contribution is a cached O(1) lookup (N5, local
  // Codex round-4 P1) — NEVER copied/mutated here; `blockSeed.ids` /
  // `blockSeed.typeCounts` stay the shared cache entry. Only the
  // spaces-scoped scan (mutates on nearly every command, so it can't be
  // cached across calls) is rebuilt per allocator construction — an O(N)
  // scan ONCE, same as before N5. Entities `isBlockNamespaceMirroredEntity`
  // flags are skipped here too (N5, local Codex round-5/round-6 — see its
  // JSDoc): a LIVE mirror of block-definition content `blockSeed` already
  // counts, checked PER-ENTITY (not per-space — a REFEDIT session's
  // not-yet-synced newly-authored entities must stay counted).
  const blockSeed = getBlockNamespaceSeed(document);
  const spaceIds = new Set<string>();
  const spaceTypeCounts = new Map<string, number>();
  for (const space of document.spaces) {
    for (const entity of space.entities) {
      if (isBlockNamespaceMirroredEntity(entity, blockSeed)) {
        continue;
      }
      spaceIds.add(entity.id);
      const typeKey = entity.type.toLowerCase();
      spaceTypeCounts.set(typeKey, (spaceTypeCounts.get(typeKey) ?? 0) + 1);
    }
  }

  // Ids minted DURING this allocator's lifetime — kept SEPARATE from
  // `spaceIds` / `blockSeed.ids` so neither the per-call space scan nor the
  // cached block seed is ever mutated (mutating the cached `blockSeed.ids`
  // would corrupt it for every OTHER caller sharing the same
  // `document.blocks` reference).
  const mintedIds = new Set<string>();
  const isLive = (id: string): boolean =>
    spaceIds.has(id) || blockSeed.ids.has(id) || mintedIds.has(id);

  // Per-prefix advancing counter. Seeded lazily on first `next(prefix)` from
  // the immutable spaceTypeCounts + blockSeed.typeCounts snapshots so the
  // base matches nextGeometryEntityId.
  const counters = new Map<string, number>();

  return {
    next(prefix: string): string {
      let counter = counters.get(prefix);
      if (counter === undefined) {
        counter =
          (spaceTypeCounts.get(prefix) ?? 0) + (blockSeed.typeCounts.get(prefix) ?? 0) + 1;
      }
      let candidate = `${prefix}-${counter}`;
      while (isLive(candidate)) {
        counter += 1;
        candidate = `${prefix}-${counter}`;
      }
      // Reserve the minted id so a later mint (any prefix) never re-issues it,
      // and advance the counter past it (same monotonic progression the
      // per-call recompute produces once the new entity is appended).
      mintedIds.add(candidate);
      counters.set(prefix, counter + 1);
      return candidate;
    },
  };
}

export function appendGeometryEntity(
  document: DrawingDocument,
  entity: DrawingEntityInput,
): DrawingDocument {
  // Phase 7.2 PR-E3 (#16) — accept `DrawingEntityInput` (z.input) so
  // callers can construct entity literals without supplying every
  // field that has a schema-level default (`layer`, `color`,
  // `linetype`, `lineweight`, `transparency`, `thickness`, etc). The
  // schema parse fills in the defaults and produces the canonical
  // `DrawingEntity` shape that `assembleDrawingDocument` consumes.
  //
  // The single-entity parse cost is dominated by the surrounding
  // `assembleDrawingDocument` call (full-document parse in DEV;
  // shallow validate in PROD) — adding a single-entity parse here is
  // not perf-significant.
  const parsedEntity = drawingEntitySchema.parse(entity);

  if (!document.spaces.some((space) => space.id === parsedEntity.spaceId)) {
    return document;
  }

  return assembleDrawingDocument({
    ...document,
    spaces: document.spaces.map((space) =>
      space.id === parsedEntity.spaceId
        ? {
            ...space,
            entities: [...space.entities, parsedEntity],
          }
        : space,
    ),
  });
}

/**
 * Batch sibling of {@link appendGeometryEntity}: append MANY entities to the
 * document with a SINGLE `assembleDrawingDocument` validation pass.
 *
 * The per-entity {@link appendGeometryEntity} runs `assembleDrawingDocument`
 * once PER entity (full-document parse in DEV; invariant validate in PROD), so
 * appending K entities one-by-one inside a reducer loop is O(N·K) ≈ O(N²).
 * This helper does the SAME per-entity `drawingEntitySchema.parse` + the SAME
 * missing-space drop, but assembles ONCE → O(N) for the whole batch.
 *
 * Byte-identity with the per-entity loop is exact:
 *   - Each input is parsed via `drawingEntitySchema.parse` (identical to the
 *     single path) so schema defaults are filled identically.
 *   - An entity whose `spaceId` is absent from `document.spaces` is DROPPED
 *     (no-op parity with the single path's space-existence guard) — it is not
 *     appended and does not appear in the result.
 *   - Surviving entities are concatenated onto their target space's `entities`
 *     in input order, so the final entity order matches a per-entity loop that
 *     appended them in the same order.
 *
 * Returns the input `document` unchanged when `entities` is empty.
 */
export function appendGeometryEntities(
  document: DrawingDocument,
  entities: readonly DrawingEntityInput[],
): DrawingDocument {
  if (entities.length === 0) {
    return document;
  }

  const existingSpaceIds = new Set(document.spaces.map((space) => space.id));
  // Group the parsed survivors by target spaceId, preserving input order.
  const bySpace = new Map<string, DrawingEntity[]>();
  for (const entity of entities) {
    const parsedEntity = drawingEntitySchema.parse(entity);
    // Same fail-soft guard as appendGeometryEntity: an entity targeting a
    // space that does not exist is silently dropped (no-op parity).
    if (!existingSpaceIds.has(parsedEntity.spaceId)) {
      continue;
    }
    const bucket = bySpace.get(parsedEntity.spaceId);
    if (bucket) {
      bucket.push(parsedEntity);
    } else {
      bySpace.set(parsedEntity.spaceId, [parsedEntity]);
    }
  }

  // Nothing survived the space guard → no-op (identical to a loop where every
  // append hit the missing-space short-circuit).
  if (bySpace.size === 0) {
    return document;
  }

  return assembleDrawingDocument({
    ...document,
    spaces: document.spaces.map((space) => {
      const appended = bySpace.get(space.id);
      if (!appended) {
        return space;
      }
      return { ...space, entities: [...space.entities, ...appended] };
    }),
  });
}

/** One block-insert copy's per-instance fields (the fields that vary across a
 * coalesced MINSERT run — everything else, `spaceId`/`blockName`/`color`, is
 * shared at the batch level). Mirrors `geometry/create-block-insert`'s
 * per-copy fields minus the fields hoisted to the batch. */
export interface BlockInsertCopySpec {
  insertionPoint: { x: number; y: number };
  scaleX: number;
  scaleY: number;
  rotation: number;
}

/**
 * Build ONE Block (INSERT) entity literal — the shared one-truth used by BOTH
 * the single `geometry/create-block-insert` reducer case (id minted via
 * `nextGeometryEntityId`) and the T6b batch sibling
 * `geometry/create-block-inserts-batch` (ids minted via the once-seeded
 * `createEntityIdAllocator`, appended for the whole run via
 * `appendGeometryEntities`). Extracting this keeps entity-construction a
 * SINGLE source of truth — the batch path must never re-derive block-insert
 * field defaults independently of the single-insert path.
 *
 * `customProperties` is seeded from the referenced block's dynamic metadata
 * (same as the single-insert path) so every copy is immediately usable with
 * DYNSET / property palette / evaluator overlay — missing metadata (static
 * block or block-def not yet loaded) yields an empty bag, matching historical
 * behaviour. `color` defaults to ByLayer when absent (CECOLOR is stamped onto
 * the command BEFORE this is called — see `stampCeColorOntoCreateCommand`
 * and `CE_COLOR_STAMPABLE_CREATE` in `cecolorStore.ts`).
 */
export function buildBlockInsertEntityInput(
  document: DrawingDocument,
  id: string,
  spaceId: string,
  blockName: string,
  color: EntityColor | undefined,
  copy: BlockInsertCopySpec,
  // T7 (agent-bridge) local Codex round-18 P2 (post-T6b-merge regression
  // catch) — the CLAYER sibling of `color` above. Optional, defaulting
  // parameter position AFTER `copy` (not inserted between `color`/`copy`)
  // so this stays a purely-additive signature change for every existing
  // caller.
  layer?: string,
): DrawingEntityInput {
  const blockDef = document.blocks[blockName];
  const customProperties = initCustomPropertiesFromMetadata(
    blockDef?.dynamicMetadata,
  );
  return {
    id,
    type: "Block",
    spaceId,
    blockName,
    insertionPoint: { x: copy.insertionPoint.x, y: copy.insertionPoint.y },
    scaleX: copy.scaleX,
    scaleY: copy.scaleY,
    rotation: copy.rotation,
    customProperties,
    // T7 (agent-bridge) local Codex round-18 P2 — stamp CLAYER onto the
    // INSERT ENTITY, mirroring the CECOLOR stamp directly below exactly.
    // "0" default when absent — mirrors the single-insert reducer case
    // verbatim (matches `command.layer ?? "0"`).
    layer: layer ?? "0",
    // CECOLOR — stamp the current colour onto the INSERT ENTITY. A ByLayer
    // insert colour lets children render with their own colours; an explicit
    // CECOLOR on the INSERT colours its BYBLOCK children (AutoCAD-correct —
    // we stamp the insert entity colour and do NOT recurse into the block
    // definition). Default ByLayer when absent — mirrors the single-insert
    // reducer case verbatim.
    color: color ?? { kind: "byLayer" },
  };
}

export function removeGeometryEntitiesById(
  document: DrawingDocument,
  entityIds: string[],
): DrawingDocument {
  const idsToDelete = new Set(entityIds);

  // Fast path: deleting entities cannot introduce any invariant
  // violation the Zod schema would catch — the surviving entities
  // are a subset of an already-validated set. Skip the schema walk
  // on hot multi-delete paths.
  return assembleDrawingDocument({
    ...document,
    spaces: document.spaces.map((space) => ({
      ...space,
      entities: space.entities.filter((entity) => !idsToDelete.has(entity.id)),
    })),
  });
}

/**
 * Replace the single entity `targetId` in space `spaceId` with `replacements`,
 * spliced IN PLACE at the target's draw-order slot (Codex #497 P2). Minted TRIM
 * survivors — a mid-curve split fragment, or the Arc that replaces a trimmed
 * Circle — must inherit the trimmed entity's position in the space's entity list
 * rather than being appended to the tail: a tail append renders / exports the
 * fragment ABOVE every entity that was originally drawn AFTER the trimmed one,
 * silently changing the visible draw order. Each replacement is schema-parsed
 * (defaults filled) exactly as {@link appendGeometryEntities} does, then a single
 * `assembleDrawingDocument` validation pass runs. When `targetId` is absent from
 * `spaceId` no slot matches and the document is returned entity-for-entity
 * unchanged (the replacements are dropped — the caller minted them for a slot
 * that no longer exists).
 */
export function replaceGeometryEntityInPlace(
  document: DrawingDocument,
  spaceId: string,
  targetId: string,
  replacements: readonly DrawingEntityInput[],
): DrawingDocument {
  const parsed = replacements.map((entity) => drawingEntitySchema.parse(entity));
  return assembleDrawingDocument({
    ...document,
    spaces: document.spaces.map((space) =>
      space.id !== spaceId
        ? space
        : {
            ...space,
            entities: space.entities.flatMap((entity) =>
              entity.id === targetId ? parsed : [entity],
            ),
          },
    ),
  });
}

/** Per-entity `updated` diff record (mirrors the reducer mutation shape). */
export interface ClearedViewportClip {
  spaceId: string;
  prev: DrawingEntity;
  next: DrawingEntity;
}

/** Result of {@link clearDanglingViewportClips}: the document plus the per-VP
 * diffs the caller must thread into `mutations.updated` (the compact cache is
 * patched incrementally from those diffs — omitting them would leave the stale
 * clip handle in the store and re-emit the dangling 340 on export). */
export interface ClearDanglingViewportClipsResult {
  document: DrawingDocument;
  updated: ClearedViewportClip[];
}

/**
 * Normalize a DXF handle for comparison — handles are hex and producers are
 * inconsistent about case (`2F` vs `2f`, Codex #324 P2). Mirrors the dxf-io
 * `normalizeHandle` `trim().toLowerCase()` one-truth; kept inline to avoid a new
 * apps/web → dxf-io dependency edge for a 1-line idiom. Shared by BOTH viewport
 * clip referential-integrity walks below.
 */
function normHandle(h: string): string {
  return h.trim().toLowerCase();
}

/**
 * SHARED per-viewport clip-diff walk — the ONE-TRUTH skeleton for the viewport
 * clip referential-integrity helpers ({@link clearDanglingViewportClips} ERASE
 * side + {@link recomputeObjectDerivedViewportClips} EDIT/MOVE side). Walks every
 * space, runs `decide` on each Viewport, and — when `decide` returns a DIFFERENT
 * viewport (`null` = unchanged, so is a returned-same reference) — records the
 * prev→next diff and swaps the result into a rebuilt space. Returns the SAME
 * document reference + empty `updated` when nothing changed, so both callers keep
 * their fast path; each caller owns any follow-up (e.g. SPATIAL_FILTER detach).
 */
function mapViewportClipDiffs(
  document: DrawingDocument,
  decide: (vp: ViewportEntity) => ViewportEntity | null,
): ClearDanglingViewportClipsResult {
  const updated: ClearedViewportClip[] = [];
  const nextSpaces = document.spaces.map((space) => {
    let spaceChanged = false;
    const nextEntities = space.entities.map((entity) => {
      if (entity.type !== "Viewport") {
        return entity;
      }
      const next = decide(entity as ViewportEntity);
      if (next === null || next === entity) {
        return entity;
      }
      spaceChanged = true;
      updated.push({ spaceId: space.id, prev: entity, next });
      return next;
    });
    return spaceChanged ? { ...space, entities: nextEntities } : space;
  });
  if (updated.length === 0) {
    return { document, updated };
  }
  // Clearing / reshaping optional clip fields on a survivor cannot introduce an
  // invariant violation the Zod schema would catch, so no re-parse (same
  // rationale as `removeGeometryEntitiesById`'s fast path).
  return { document: { ...document, spaces: nextSpaces }, updated };
}

/**
 * Referential-integrity cleanup for a VIEWPORT clip after the boundary entity
 * it points at has been ERASED (PR #318 residual R2).
 *
 * A non-rectangular VIEWPORT clip carries a typed group-340 hard-pointer
 * (`clippingBoundaryHandle`) — and, for pre-typed-field documents, a legacy
 * `extraGroups`-340 carrier — to the SEPARATE paperspace boundary entity
 * (LWPOLYLINE / CIRCLE / ELLIPSE / closed SPLINE) that defines the clip path,
 * plus the group-90 `0x10000` non-rectangular-clip bit. The DXF writer
 * re-emits the 340 hard-pointer verbatim; if the boundary entity has been
 * deleted, that 340 becomes a DANGLING reference to a missing handle on the
 * next export (an invalid file). The erase reducers' `removeGeometryEntitiesById`
 * only filters ids — it does NO referential cleanup — so this helper must run
 * after a delete to clear any viewport whose clip now dangles.
 *
 * Because an imported entity's `id` IS its DXF handle (dxfBridge import:
 * `entityId = entity.common.handle?.trim() || nextId(...)`; export:
 * `handle: entity.id`), a deleted entity's id is exactly the value a dangling
 * `clippingBoundaryHandle` would hold — so the deleted-id set is matched
 * directly against the survivors' handles. The literal `"0"` no-clip sentinel
 * is never a real boundary id, so it is not in the deleted set and is left
 * untouched (preserve-source); `invalidateViewportClip` additionally protects
 * it. Reuses the shared `invalidateViewportClip` (typed 340 + 0x10000 bit +
 * legacy extraGroups-340) — no duplicated clip-artefact logic.
 *
 * Returns the SAME document reference + an empty `updated` when no surviving
 * viewport pointed at a deleted handle (the common case) so callers can keep
 * their fast path. When a viewport is cleaned, the returned `updated` carries
 * its prev→next diff so the caller threads it into `mutations.updated`.
 */
export function clearDanglingViewportClips(
  document: DrawingDocument,
  deletedEntityIds: string[],
): ClearDanglingViewportClipsResult {
  // DXF handles are hex; producers are inconsistent about case (`2F` vs `2f`)
  // (Codex #324 P2). Normalize BOTH the deleted-id set and every clip-handle
  // comparison so a casing difference never makes a dangling 340 escape cleanup.
  const deletedHandles = new Set(deletedEntityIds.map(normHandle));
  if (deletedHandles.size === 0) {
    return { document, updated: [] };
  }

  const { document: nextDocument, updated } = mapViewportClipDiffs(
    document,
    (vp) => {
      // A dangling clip = a boundary handle (typed 340 OR a legacy
      // extraGroups-340 carrier from a pre-typed-field document) that now points
      // at a DELETED entity. (A literal "0" sentinel is never a deleted id, so
      // it never matches — preserve-source.)
      // A LIVE typed `clippingBoundaryHandle` (present, incl the literal "0"
      // no-clip sentinel) is AUTHORITATIVE — the legacy `extraGroups`-340
      // carrier is consulted ONLY when there is NO typed handle. Otherwise a
      // STALE legacy 340 pointing at a deleted entity would wrongly clear a
      // viewport whose typed clip points at a DIFFERENT, surviving boundary
      // (Codex #324 P2 — ignore stale legacy 340 when the typed clip is live).
      const typedPresent = vp.clippingBoundaryHandle !== undefined;
      const typedDangles =
        typedPresent &&
        vp.clippingBoundaryHandle !== "0" &&
        deletedHandles.has(normHandle(vp.clippingBoundaryHandle!));
      const legacyDangles =
        !typedPresent &&
        vp.extraGroups !== undefined &&
        vp.extraGroups.some(
          (g) => g.code === 340 && deletedHandles.has(normHandle(g.value)),
        );
      if (!typedDangles && !legacyDangles) {
        return null;
      }
      const cleaned = invalidateViewportClip(vp);
      return cleaned !== vp ? cleaned : null;
    },
  );

  if (updated.length === 0) {
    return { document, updated };
  }

  // Codex #326 r3 P2 #3 — for a cleaned viewport whose clip was SPATIAL_FILTER-
  // sourced, the OBJECTS round-trip (ext-dict → ACAD_FILTER → SPATIAL_FILTER on
  // `rawObjectGroups`) still persists the chain after the entity-side clip fields
  // are cleared. Detach it at the document level (keyed by each cleaned viewport's
  // `extensionDictionaryHandle`, preserving any unrelated ext-dict entries) so the
  // dangling clip stays gone across save+reload, not just in-memory.
  // Every `updated.next` came from `invalidateViewportClip(vp)` on a Viewport, so
  // it is a ViewportEntity (the `ClearedViewportClip.next` field is typed as the
  // wider DrawingEntity union — narrow it for the detach helper).
  const cleanedViewports = updated
    .map((u) => u.next)
    .filter((e): e is ViewportEntity => e.type === "Viewport");
  return {
    document: detachInvalidatedViewportClipObjects(nextDocument, cleanedViewports),
    updated,
  };
}

/**
 * Associative RE-DERIVATION (AutoCAD parity) of an OBJECT-derived VIEWPORT clip
 * after the boundary ENTITY it points at (typed group-340
 * `clippingBoundaryHandle`) had its GEOMETRY edited or MOVED **in place** — the
 * EDIT / MOVE sibling of {@link clearDanglingViewportClips} (which owns the ERASE
 * side).
 *
 * @codex #496 P2 (forward object-mode divergence). VPCLIP "Select clipping
 * object" (`applyViewportClipSetPolygon`, object mode) FREEZES the boundary
 * object's CURRENT vertices into the viewport's `clipBoundaryPaperSpace` /
 * `clipPath` AND records the object's LIVE 340 handle so the clip survives
 * save/reopen (@codex #496 P1). Nothing re-derived that frozen polygon when the
 * boundary object was later moved/edited, yet the DXF writer re-emits the 340 and
 * reimport re-resolves the EDITED object — so the reopened file clipped
 * DIFFERENTLY from the in-session render. Re-resolving the paper polygon here
 * through the SAME shared resolver the producer + store redo-freeze use keeps the
 * two in lockstep: the in-session clip now tracks its boundary object exactly as a
 * save+reopen would (AutoCAD's clip stays associative to its boundary object).
 *
 * Scope — ONLY a RESOLVED / frozen object clip (a viewport that already carries a
 * `clipBoundaryPaperSpace`) is re-derived. A HANDLE-ONLY imported clip (340
 * present, geometry not yet resolved into the typed fields — the deferred
 * slice-2/3 resolution) is deliberately left untouched: it has no frozen polygon
 * to diverge (its clip is resolved from the 340 at import by construction), so
 * MATERIALISING geometry onto it on an unrelated edit would be scope creep and
 * break the handle-only preservation contract. The literal `"0"` no-clip sentinel
 * is likewise never re-derived (preserve-source).
 *
 * When the edit turns the boundary into an INVALID clip source (opened / <3
 * vertices / degenerate area — `resolveVpclipBoundaryPolygon` → `null`) the clip
 * is INVALIDATED (dropped) exactly like the erase path, so a later save can never
 * emit a 340 pointing at an object that no longer forms a clip region.
 *
 * Returns the SAME document reference + empty `updated` when no frozen object clip
 * pointed at a changed entity (the overwhelmingly common case), so callers keep
 * their fast path. Threaded `updated` diffs mirror {@link clearDanglingViewportClips}
 * so the compact cache + undo history capture the re-derived clip.
 */
export function recomputeObjectDerivedViewportClips(
  document: DrawingDocument,
  changedEntities: ReadonlyArray<{ spaceId: string; id: string }>,
): ClearDanglingViewportClipsResult {
  // DXF handles are case-insensitive hex; NORMALIZE both sides so a casing
  // difference never makes a moved boundary miss its clip (mirrors the
  // `clearDanglingViewportClips` one-truth).
  const changedRealIdByHandle = new Map<string, string>();
  for (const c of changedEntities) {
    changedRealIdByHandle.set(normHandle(c.id), c.id);
  }
  if (changedRealIdByHandle.size === 0) {
    return { document, updated: [] };
  }

  const invalidatedViewports: ViewportEntity[] = [];
  const { document: nextDocument, updated } = mapViewportClipDiffs(
    document,
    (vp) => {
      const handle = vp.clippingBoundaryHandle;
      // Only a RESOLVED / frozen object clip can diverge: it must carry a real
      // boundary handle (never the "0" no-clip sentinel) AND an already-resolved
      // `clipBoundaryPaperSpace`. Handle-only imported clips have no frozen polygon
      // and are resolved from the 340 at import by construction — skip them.
      if (
        handle === undefined ||
        handle === "0" ||
        !vp.clipBoundaryPaperSpace ||
        vp.clipBoundaryPaperSpace.length < 3
      ) {
        return null;
      }
      const changedRealId = changedRealIdByHandle.get(normHandle(handle));
      if (changedRealId === undefined) {
        return null; // this viewport's boundary object did not change
      }
      // Re-resolve the PAPER polygon from the (edited) boundary object via the
      // ONE-TRUTH resolver — an invalid boundary (open / <3 verts / degenerate /
      // wrong space) yields null → invalidate, exactly like the erase path.
      const polygon = resolveVpclipBoundaryPolygon(document, vp.id, {
        boundaryEntityId: changedRealId,
      });
      let nextVp: ViewportEntity;
      if (polygon) {
        const withBoundary: ViewportEntity = {
          ...vp,
          clipBoundaryPaperSpace: polygon,
        };
        const modelPath = recomputeNativeClipPath(withBoundary);
        nextVp =
          modelPath && modelPath.length >= 3
            ? {
                ...withBoundary,
                clipPath: modelPath.map((p) => ({ x: p.x, y: p.y })),
              }
            : withBoundary;
      } else {
        nextVp = invalidateViewportClip(vp);
      }
      // No-op guard: a NON-geometry edit on the boundary object (colour / layer /
      // …) re-resolves the IDENTICAL polygon → identical derived clip; skip the
      // spurious diff so it never churns history / the compact cache. Both objects
      // share `vp`'s key order (the overrides replace existing keys in place), so
      // a structural JSON compare is exact (same idiom as `entitiesShapeEqual`).
      if (JSON.stringify(vp) === JSON.stringify(nextVp)) {
        return null;
      }
      if (!polygon) {
        invalidatedViewports.push(nextVp);
      }
      return nextVp;
    },
  );

  if (updated.length === 0) {
    return { document, updated };
  }
  // Detach the persisted SPATIAL_FILTER OBJECTS chain ONLY for clips that were
  // INVALIDATED (dropped) — a re-derived (still-valid) clip keeps its chain.
  if (invalidatedViewports.length === 0) {
    return { document: nextDocument, updated };
  }
  return {
    document: detachInvalidatedViewportClipObjects(
      nextDocument,
      invalidatedViewports,
    ),
    updated,
  };
}
